/**
 * Learn more about theme events at
 * https://help.switchthemes.co/baseline/developer/theme-events/overview.html
 */

window.theme = window.theme || {};

window.theme.events = {
  version: '1.0',
};

document.addEventListener('theme:update:cart', async () => {
  document.dispatchEvent(new CustomEvent('baseline:cart:lock'));

  try {
    const response = await fetch(
      `${window.location.pathname}?sections=cart-items,cart-footer,cart-item-count,cart-live-region`
    );
    const data = await response.json();

    if (!data.status) {
      document.body.dispatchEvent(
        new CustomEvent('baseline:cart:update', {
          bubbles: true,
          detail: {
            response: {
              sections: {
                ...data,
              },
            },
          },
        })
      );
    }
  } catch (e) {
    console.error(e);
    document.getElementById('cart-errors').textContent =
      theme.strings.cartError;
    document.dispatchEvent(
      new CustomEvent('theme:cart:error:other', {
        detail: {
          message: theme.strings.cartError,
          error: e,
        },
      })
    );
  } finally {
    document.dispatchEvent(new CustomEvent('baseline:cart:unlock'));
  }
});

const getCartDrawerEl = () => {
  return document.querySelector('[data-cart-drawer]');
};

document.body.addEventListener('cart-will-open', async () => {
  document.dispatchEvent(
    new CustomEvent('theme:cart-drawer:opening', {
      detail: {
        cartDrawerEl: getCartDrawerEl(),
      },
    })
  );
});

document.body.addEventListener('cart-is-open', async () => {
  document.dispatchEvent(
    new CustomEvent('theme:cart-drawer:open', {
      detail: {
        cartDrawerEl: getCartDrawerEl(),
      },
    })
  );
});

document.body.addEventListener('cart-will-close', () => {
  document.dispatchEvent(
    new CustomEvent('theme:cart-drawer:closing', {
      detail: {
        cartDrawerEl: getCartDrawerEl(),
      },
    })
  );
});

document.body.addEventListener('cart-is-closed', () => {
  document.dispatchEvent(
    new CustomEvent('theme:cart-drawer:closed', {
      detail: {
        cartDrawerEl: getCartDrawerEl(),
      },
    })
  );
});

document.addEventListener('theme:open:cart-drawer', () => {
  if (
    window.theme.settings.cartType === 'drawer' &&
    Alpine.store('modals').modals.cart
  ) {
    Alpine.store('modals').closeAll();

    Alpine.store('modals').open('cart');
  }
});

document.addEventListener('theme:close:cart-drawer', () => {
  if (
    window.theme.settings.cartType === 'drawer' &&
    Alpine.store('modals').modals.cart
  ) {
    Alpine.store('modals').close('cart');
  }
});

const cartChangeEventHandler = (e) => {
  const { cartItemCount, itemsRootEl, lineItemEl, variantId, key } = e.detail;

  document.dispatchEvent(
    new CustomEvent('theme:cart:change', {
      detail: {
        type: e.type,
        cartItemCount,
        itemsRootEl,
        lineItemEl,
        variantId,
        key,
        originalDetail: e.detail,
      },
    })
  );
};

document.addEventListener('theme:product:add', cartChangeEventHandler);
document.addEventListener('theme:line-item:change', cartChangeEventHandler);

const cartErrorEventHandler = (e) => {
  document.dispatchEvent(
    new CustomEvent('theme:cart:error', {
      detail: {
        type: e.type,
        message: e.detail.message,
        originalDetail: e.detail,
      },
    })
  );
};

document.addEventListener(
  'theme:product:error:add-to-cart',
  cartErrorEventHandler
);

document.addEventListener('theme:line-item:error', cartErrorEventHandler);
document.addEventListener('theme:cart:error:other', cartErrorEventHandler);

/**
 * Set `window.themeEventsDebugMode` to `true` to log events
 * outside the theme editor
 */

if (Shopify.designMode || window.themeEventsDebugMode) {
  const themeEvents = [
    'theme:variant:change',
    'theme:product:add',
    'theme:line-item:change',
    'theme:cart:change',
    'theme:cart:update',
    'theme:cart-drawer:opening',
    'theme:cart-drawer:open',
    'theme:cart-drawer:closing',
    'theme:cart-drawer:closed',
    'theme:product:error:add-to-cart',
    'theme:line-item:error',
    'theme:cart:error:other',
    'theme:cart:error',
  ];

  const aggregateEvents = ['theme:cart:change', 'theme:cart:error'];

  for (const eventName of themeEvents) {
    let color;

    if (eventName.includes('error')) {
      color = '#ef4444';
    } else {
      color = '#22c55e';
    }

    const message = [
      `%c\u25CF %c${eventName}`,
      `color: ${color};`,
      'font-weight: bold;',
    ];

    if (aggregateEvents.includes(eventName)) {
      document.addEventListener(eventName, (e) => {
        console.groupCollapsed(...message);
        console.log(
          `Underlying event: %c${e.detail.type}`,
          'font-weight: bold;'
        );
        console.log('Event detail:', e.detail);
        console.groupEnd();
      });
    } else {
      document.addEventListener(eventName, (e) => {
        console.log(...message, e.detail);
      });
    }
  }
}

/**
 * Petit Pli: custom analytics events for the product-page features, so their
 * effect on sales can be measured (size guide, care guide, Watch it grow,
 * Complete the set, gift options, FAQ, and picking a sold-out combination).
 *
 * Events go through Shopify's Customer events (`Shopify.analytics.publish`),
 * so they respect the visitor's consent. Nothing is sent anywhere until a
 * custom pixel in Settings > Customer events subscribes to them, e.g.
 * `analytics.subscribe('pp_size_guide_open', (event) => { ... })`.
 * Event names: pp_size_guide_open, pp_care_open, pp_watch_it_grow_open,
 * pp_complete_set_add, pp_gift_option_toggle, pp_faq_open, pp_sold_out_select.
 * Publishing is deferred off the tap so it never slows the interaction.
 */
(() => {
  const publish = (name, data) => {
    const run = () => {
      try {
        const analytics = window.Shopify && window.Shopify.analytics;
        if (!analytics || typeof analytics.publish !== 'function') return;
        const meta = (window.ShopifyAnalytics && window.ShopifyAnalytics.meta) || {};
        const variant = new URLSearchParams(window.location.search).get('variant');
        analytics.publish(name, {
          product_id: meta.product ? String(meta.product.id) : null,
          variant_id: variant || null,
          ...data,
        });
      } catch (e) {
        /* analytics must never break the page */
      }
    };
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(run, { timeout: 2000 });
    } else {
      setTimeout(run, 0);
    }
  };

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    // The gift-bundle guide's Care button shares the size-guide button style
    // (pp-sg__open); it is told apart by its data-pp-bg-open id ("…-care").
    if (target.closest('.pp-care__open, [data-pp-bg-open$="-care"]')) {
      publish('pp_care_open', {});
    } else if (target.closest('.pp-sg__open')) {
      publish('pp_size_guide_open', {});
    } else if (target.closest('[data-pp-witg-open]')) {
      const el = target.closest('[data-pp-witg-open]');
      publish('pp_watch_it_grow_open', {
        trigger: el.hasAttribute('data-pp-witg-pill') ? 'pill' : 'play',
      });
    } else if (target.closest('[data-pp-set-add]')) {
      publish('pp_complete_set_add', {});
    } else {
      const summary = target.closest('details.pp-faq > summary');
      if (summary && !summary.parentElement.open) {
        publish('pp_faq_open', {
          question: (summary.textContent || '').trim().slice(0, 100),
        });
      }
    }
  });

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !target.matches('[data-pp-gift-check]')) return;
    const option = target.closest('[data-pp-gift-kind]');
    publish('pp_gift_option_toggle', {
      kind: option ? option.getAttribute('data-pp-gift-kind') : null,
      checked: target.checked,
    });
  });

  // Shoppers landing on, or choosing, a sold-out size/colour: a demand signal.
  document.addEventListener('theme:variant:change', (event) => {
    const variant = event.detail && event.detail.variant;
    if (!variant || variant.available || !event.detail.previousVariant) return;
    publish('pp_sold_out_select', {
      variant_id: String(variant.id),
      variant_title: variant.title || null,
    });
  });
})();
